import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { animeList } from "@/mocks/anime";
import { newsList } from "@/mocks/news";

interface SearchBarProps {
  initialValue?: string;
  size?: "md" | "lg";
  placeholder?: string;
}

export default function SearchBar({
  initialValue = "",
  size = "md",
  placeholder = "搜索动漫 / 资讯关键词",
}: SearchBarProps) {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState(initialValue);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setKeyword(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const suggestions = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return { anime: [], news: [] };
    return {
      anime: animeList
        .filter(
          (a) =>
            a.title.toLowerCase().includes(q) ||
            a.category.toLowerCase().includes(q) ||
            a.tags.some((t) => t.toLowerCase().includes(q))
        )
        .slice(0, 4),
      news: newsList.filter((n) => n.title.toLowerCase().includes(q)).slice(0, 3),
    };
  }, [keyword]);

  const hasSuggestions = suggestions.anime.length > 0 || suggestions.news.length > 0;

  const go = (q: string) => {
    const value = q.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setOpen(false);
  };

  const heightClass = size === "lg" ? "h-12 md:h-14" : "h-11";

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="relative">
        <input
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => e.key === "Enter" && go(keyword)}
          placeholder={placeholder}
          className={`w-full ${heightClass} pl-12 pr-24 rounded-full bg-background-50 border border-background-200 text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400`}
        />
        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400 text-lg" />
        <button
          onClick={() => go(keyword)}
          className={`absolute right-1.5 top-1/2 -translate-y-1/2 rounded-full bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap ${
            size === "lg" ? "h-9 md:h-11 px-5 text-sm" : "h-8 px-4 text-sm"
          }`}
        >
          搜索
        </button>
      </div>

      {open && keyword.trim() && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-40 bg-background-50 rounded-lg border border-background-200/80 py-2 max-h-96 overflow-y-auto animate-fade-in">
          {hasSuggestions ? (
            <>
              {suggestions.anime.length > 0 && (
                <div className="px-2">
                  <p className="px-2 py-1.5 text-xs text-foreground-400">动漫</p>
                  {suggestions.anime.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => go(a.title)}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-background-100 text-left cursor-pointer"
                    >
                      <span className="w-9 h-9 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img src={a.cover} alt={a.title} className="w-full h-full object-cover object-top" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-foreground-900 truncate">{a.title}</span>
                        <span className="block text-xs text-foreground-400 truncate">
                          {a.category} · {a.year} · {a.region}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300" />
                    </button>
                  ))}
                </div>
              )}
              {suggestions.news.length > 0 && (
                <div className="px-2 mt-1">
                  <p className="px-2 py-1.5 text-xs text-foreground-400">资讯</p>
                  {suggestions.news.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => go(n.title)}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-md hover:bg-background-100 text-left cursor-pointer"
                    >
                      <span className="w-9 h-9 rounded-md bg-accent-100 flex items-center justify-center shrink-0">
                        <i className="ri-newspaper-line text-accent-700" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-foreground-900 truncate">{n.title}</span>
                        <span className="block text-xs text-foreground-400 truncate">
                          {n.category} · {n.publishedAt}
                        </span>
                      </span>
                      <i className="ri-arrow-right-s-line text-foreground-300" />
                    </button>
                  ))}
                </div>
              )}
              <button
                onClick={() => go(keyword)}
                className="w-full mt-1 px-4 py-2.5 text-sm text-primary-600 text-left hover:bg-primary-50 cursor-pointer border-t border-background-200/70"
              >
                查看「{keyword.trim()}」的全部结果
                <i className="ri-arrow-right-line ml-1" />
              </button>
            </>
          ) : (
            <div className="px-4 py-4 text-sm text-foreground-500 flex items-center gap-2">
              <i className="ri-emotion-sad-line text-lg text-foreground-400" />
              没有找到与「{keyword.trim()}」相关的内容
            </div>
          )}
        </div>
      )}
    </div>
  );
}